import React, { useRef, useState, useCallback } from 'react';
import { usePlaidLink, PlaidLinkOptions, PlaidLinkOnSuccessMetadata } from 'react-plaid-link';
import { plaidApi } from '../../services/api';

interface BankAccount {
  id: string;
  institution_name: string;
  last4: string;
  account_type: string;
  status: string;
}

interface BankLinkButtonProps {
  onLinked: (accounts: BankAccount[]) => void;
  variant?: 'primary' | 'secondary';
}

export default function BankLinkButton({ onLinked, variant = 'primary' }: BankLinkButtonProps) {
  const [loading, setLoading] = useState(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  const tokenRequestInFlight = useRef(false);
  const lastTokenRequestAt = useRef(0);

  const createLinkToken = useCallback(async () => {
    if (tokenRequestInFlight.current) return;

    const now = Date.now();
    if (now - lastTokenRequestAt.current < 2500) {
      return;
    }

    tokenRequestInFlight.current = true;
    lastTokenRequestAt.current = now;

    try {
      setInitError(null);
      const response = await plaidApi.createLinkToken();

      if (response.data?.success && response.data?.data?.link_token) {
        setLinkToken(response.data.data.link_token);
        console.log('🔑 Plaid Link token created');
      } else {
        console.error('Failed to create link token:', response);
        setInitError('Could not initialize the bank connection. Please try again.');
      }
    } catch (error) {
      console.error('Error creating link token:', error);
      setInitError('Could not initialize the bank connection. Please try again.');
    }
    finally {
      tokenRequestInFlight.current = false;
    }
  }, []);

  // Proactively create a link token so Plaid can become ready without requiring a second click.
  React.useEffect(() => {
    void createLinkToken();
  }, [createLinkToken]);

  // Handle successful Plaid Link connection
  const onSuccess = useCallback(async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
    setLoading(true);
    setLinkError(null);
    console.log('✅ Plaid Link success:', { publicToken, metadata });

    try {
      // Exchange the public token for an access token
      const response = await plaidApi.exchangeToken({
        public_token: publicToken,
        accounts: metadata.accounts,
        institution: metadata.institution,
      });

      if (response.data?.success) {
        const accounts = response.data.data?.accounts || [];
        console.log(`✅ Successfully linked ${accounts.length} account(s):`, accounts);
        onLinked(accounts);

        // Refresh the link token for next use
        createLinkToken();
      } else {
        setLinkError(response.data?.error || 'Could not link your bank account. Please try again.');
      }
    } catch (error: any) {
      console.error('Bank link error:', error);
      setLinkError(error.message || 'Could not link your bank account. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [createLinkToken, onLinked]);

  // Handle Plaid Link exit
  const onExit = useCallback((error: any, metadata: any) => {
    console.log('Plaid Link exit:', { error, metadata });
    setLoading(false);
    
    if (error) {
      console.error('Plaid Link error:', error);
      // Don't surface an error for user-initiated exits
      if (error.error_code !== 'USER_CANCELED') {
        setLinkError('The bank connection was interrupted. Please try again.');
      }
    }
  }, []);

  // Plaid Link configuration
  const config: PlaidLinkOptions = {
    token: linkToken,
    onSuccess,
    onExit,
    onEvent: (eventName, metadata) => {
      console.log('📊 Plaid Link Event:', eventName, metadata);
    },
  };

  const { open, ready } = usePlaidLink(config);

  const handleClick = () => {
    if (!linkToken) {
      void createLinkToken();
      return;
    }

    if (!ready) {
      return;
    }

    setLoading(true);
    open();
  };

  const baseClasses = "inline-flex items-center justify-center rounded-md px-6 py-2.5 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const variantClasses = variant === 'primary'
    ? "bg-primary text-primary-foreground hover:bg-primary/90"
    : "border border-input bg-background text-foreground md:hover:bg-muted";

  const spinner = (
    <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );

  const errorMessage = linkError || (initError ? 'Could not initialize the bank connection. Please try again.' : null);
  const showReady = ready;

  return (
    <div>
      <button onClick={handleClick} disabled={loading} className={`${baseClasses} ${variantClasses}`}>
        {loading ? (
          <>{spinner} Connecting…</>
        ) : initError ? (
          'Retry initialization'
        ) : !showReady ? (
          <>{spinner} Initializing…</>
        ) : (
          'Link bank account'
        )}
      </button>
      {errorMessage && <p className="mt-3 text-sm text-destructive">{errorMessage}</p>}
    </div>
  );
}
